import { useQuery } from '@tanstack/react-query';
import { peopleCoreAPI } from '@/shared/lib/api';

export interface AuditLog {
  id: string;
  entity: string;
  entityId: string;
  action: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogsFilters {
  entity?: string;
  entityId?: string;
  userId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export function useAuditLogs(filters?: AuditLogsFilters) {
  return useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      const response = await peopleCoreAPI.listAuditLogs(filters);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });
}

export function useRecentAuditLogs(hours = 24, limit = 100) {
  return useQuery({
    queryKey: ['audit-logs', 'recent', hours, limit],
    queryFn: async () => {
      const response = await peopleCoreAPI.getRecentAuditLogs({ hours, limit });
      return response.data;
    },
    staleTime: 30000,
  });
}

export function useEntityAuditLogs(entity: string, entityId: string) {
  return useQuery({
    queryKey: ['audit-logs', 'entity', entity, entityId],
    queryFn: async () => {
      const response = await peopleCoreAPI.getEntityAuditLogs(entity, entityId);
      return response.data;
    },
    enabled: !!entity && !!entityId,
    staleTime: 30000,
  });
}

export function useUserAuditLogs(userId: string) {
  return useQuery({
    queryKey: ['audit-logs', 'user', userId],
    queryFn: async () => {
      const response = await peopleCoreAPI.getUserAuditLogs(userId);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 30000,
  });
}
